import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquare, Users, FileText, Music, Tag, Ticket, Calendar,
  Briefcase, Image, Download, ShoppingBag, Home, Video, ArrowRight,
  Sparkles, CheckCircle, Loader2, X, Upload, Plus, Eye, Heart,
  MessageCircle, Clock, TrendingUp, Globe, Send, Clapperboard,
  Play, Film, Zap, Target, Share2, BarChart3, Mail, Bot, Link2,
  Tv, Radio, Monitor, LinkIcon, Mail as MailIcon, Award, AlertCircle,
  Edit, Trash2, Megaphone, MegaphoneOff, ExternalLink, Filter, MoreVertical,
  Car, Truck, Sofa, Package
} from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { DragDropUpload } from '../components/ui/drag-drop-upload';
import { InventoryListings } from '../components/inventory/InventoryListings';
import { api, useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EXTERNAL_DIRECTORY_URL = process.env.REACT_APP_EXTERNAL_DIRECTORY_URL || 'https://search.aihorsepowered.com';

// Content type icons mapping
const CONTENT_ICONS = {
  discussion: MessageSquare,
  community_article: Users,
  blog_article: FileText,
  soundcloud_post: Music,
  classified: Tag,
  coupon: Ticket,
  event: Calendar,
  job_listing: Briefcase,
  photo_album: Image,
  digital_product: Download,
  product: ShoppingBag,
  property: Home,
  video: Video,
  // Best of Best Content Types
  tv_commercial: Tv,
  radio_ad: Radio,
  digital_billboard: Monitor,
  affiliate_link: LinkIcon,
  postcard: MailIcon,
};

const CONTENT_COLORS = {
  discussion: 'from-blue-500 to-cyan-500',
  community_article: 'from-green-500 to-emerald-500',
  blog_article: 'from-purple-500 to-pink-500',
  soundcloud_post: 'from-orange-500 to-red-500',
  classified: 'from-amber-500 to-yellow-500',
  coupon: 'from-pink-500 to-rose-500',
  event: 'from-indigo-500 to-violet-500',
  job_listing: 'from-teal-500 to-cyan-500',
  photo_album: 'from-rose-500 to-pink-500',
  digital_product: 'from-cyan-500 to-blue-500',
  product: 'from-emerald-500 to-green-500',
  property: 'from-violet-500 to-purple-500',
  video: 'from-red-500 to-orange-500',
  // Best of Best Content Types
  tv_commercial: 'from-amber-500 to-orange-600',
  radio_ad: 'from-green-500 to-teal-500',
  digital_billboard: 'from-blue-600 to-indigo-600',
  affiliate_link: 'from-pink-500 to-purple-500',
  postcard: 'from-cyan-500 to-blue-500',
};

// Best of Best Content Types (for special section)
const BEST_OF_BEST_TYPES = [
  { type: 'tv_commercial', name: 'TV Commercial', description: 'Broadcast your video ad on TV networks', icon: Tv },
  { type: 'radio_ad', name: 'Radio Ad', description: 'Audio ads for radio broadcast', icon: Radio },
  { type: 'digital_billboard', name: 'Digital Billboard', description: 'Display ads on digital billboards', icon: Monitor },
  { type: 'affiliate_link', name: 'Affiliate Link', description: 'Share your affiliate/referral links', icon: LinkIcon },
  { type: 'postcard', name: 'Post Card', description: 'Direct mail postcard campaigns', icon: MailIcon },
];

// Cinematic Ad Deployment Platforms
const DEPLOYMENT_PLATFORMS = [
  { id: 'directory', name: 'Best of Best Directory', icon: Globe, description: 'Primary listing hub', required: true },
  { id: 'meta', name: 'Meta (FB/IG)', icon: Share2, description: 'Facebook & Instagram Ads' },
  { id: 'google', name: 'Google Ads', icon: Target, description: 'Search & Display campaigns' },
  { id: 'tiktok', name: 'TikTok', icon: Play, description: 'Short-form video ads' },
  { id: 'email', name: 'Email Campaigns', icon: Mail, description: 'Nurture sequences' },
  { id: 'seo', name: 'Blogs & SEO', icon: FileText, description: 'Content marketing' },
];

// Cinematic Ad Components checklist
const AD_COMPONENTS = [
  { id: 'video', name: 'Cinematic Video', icon: Film, description: 'Your main video ad' },
  { id: 'image', name: 'Image Ad', icon: Image, description: 'Static image creative' },
  { id: 'headline', name: 'Headline', icon: Sparkles, description: 'Attention-grabbing hook' },
  { id: 'ad_copy', name: 'Ad Copy', icon: FileText, description: 'Persuasive copy' },
  { id: 'cta', name: 'Call to Action', icon: Zap, description: 'Clear next step' },
  { id: 'landing_page', name: 'Landing Page', icon: Globe, description: 'Conversion page' },
  { id: 'automations', name: 'Automations', icon: Bot, description: 'Follow-up sequences' },
];

export default function PublishingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contentTypes, setContentTypes] = useState([]);
  const [myContent, setMyContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(null);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showCinematicModal, setShowCinematicModal] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [credits, setCredits] = useState({ publishing_credits: 0, credit_cost_per_publish: 25 });
  const [videoSourceType, setVideoSourceType] = useState('url'); // 'url', 'upload', 'crm'
  const [selectedPlatforms, setSelectedPlatforms] = useState(['directory']);
  const [adComponents, setAdComponents] = useState({
    video: false,
    image: false,
    headline: false,
    ad_copy: false,
    cta: false,
    landing_page: false,
    automations: false,
  });
  const videoInputRef = useRef(null);
  
  // Content management state
  const [contentFilter, setContentFilter] = useState('all');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  
  // External Directory Publishing state
  const [directoryPublishStatus, setDirectoryPublishStatus] = useState({});
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    category: '',
    tags: '',
    images: [],
    image_url: '',
    video_url: '',
    video_file: null,
    crm_video_url: '',
    soundcloud_url: '',
    price: '',
    location: '',
    event_date: '',
    event_time: '',
    contact_email: '',
    external_url: '',
    is_featured: false,
    promote_to_directory: false,
    // Cinematic Ad fields
    headline: '',
    ad_copy: '',
    cta_text: '',
    cta_url: '',
    landing_page_url: '',
    automation_type: '',
  });
  
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const imageInputRef = useRef(null);
  // videoInputRef is already declared above
  
  // File upload handler
  const handleFileUpload = async (file, type) => {
    if (!file) return;
    
    const setUploading = type === 'image' ? setUploadingImage : setUploadingVideo;
    setUploading(true);
    
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: uploadFormData
      });
      
      if (response.ok) {
        const data = await response.json();
        const fileUrl = `${API_URL}/api/files/${data.filename}`;
        
        if (type === 'image') {
          setFormData(prev => ({ ...prev, image_url: fileUrl, images: [fileUrl] }));
        } else {
          setFormData(prev => ({ ...prev, video_url: fileUrl }));
        }
        toast.success(`${type === 'image' ? 'Image' : 'Video'} uploaded!`);
      } else {
        toast.error('Upload failed');
      }
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [typesRes, creditsRes] = await Promise.all([
        fetch(`${API_URL}/api/directory/content/types`),
        api.getMyCredits()
      ]);
      const typesData = await typesRes.json();
      setContentTypes(typesData);
      setCredits(creditsRes.data);
      
      // Try to load user's content
      try {
        const contentRes = await api.get('/api/directory/content?limit=100');
        const allContent = contentRes.data || [];
        setMyContent(allContent.filter(c => c.author_id === user?.id));
      } catch (contentError) {
        console.log('No published content yet');
        setMyContent([]);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectType = (type) => {
    setSelectedType(type);
    setFormData({
      ...formData,
      contact_email: user?.email || '',
    });
    setShowPublishModal(true);
  };

  const handlePublish = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.description) {
      toast.error('Please fill in title and description');
      return;
    }
    
    // Require image or video for directory promotion
    const hasMedia = formData.image_url || formData.images.length > 0 || formData.video_url || formData.crm_video_url;
    if (formData.promote_to_directory && !hasMedia) {
      toast.error('Image or video is required for directory content');
      return;
    }

    setPublishing(true);
    try {
      // Determine video URL based on source type
      let finalVideoUrl = formData.video_url;
      if (videoSourceType === 'crm') {
        finalVideoUrl = formData.crm_video_url;
      }
      
      // Collect images
      let finalImages = [...formData.images];
      if (formData.image_url && !finalImages.includes(formData.image_url)) {
        finalImages.push(formData.image_url);
      }

      const payload = {
        content_type: selectedType.type,
        title: formData.title,
        description: formData.description,
        content: formData.content,
        category: formData.category,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
        images: finalImages,
        image_url: formData.image_url || (finalImages.length > 0 ? finalImages[0] : null),
        video_url: finalVideoUrl || null,
        soundcloud_url: formData.soundcloud_url || null,
        price: formData.price ? parseFloat(formData.price) : null,
        location: formData.location || null,
        event_date: formData.event_date || null,
        event_time: formData.event_time || null,
        contact_email: formData.contact_email || null,
        external_url: formData.external_url || null,
        is_featured: formData.is_featured,
        promote_to_directory: formData.promote_to_directory,
      };

      await api.post('/api/directory/content', payload);
      toast.success(`${selectedType.name} published successfully!`);
      setShowPublishModal(false);
      setSelectedType(null);
      resetFormData();
      loadData(); // This will also refresh credits
    } catch (error) {
      // Handle insufficient credits error
      if (error.response?.status === 402) {
        toast.error(error.response.data?.detail || 'Insufficient publishing credits');
      } else {
        toast.error('Failed to publish content');
      }
    } finally {
      setPublishing(false);
    }
  };

  const handleCinematicPublish = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.title) {
      toast.error('Please enter a campaign title');
      return;
    }
    
    // Check video source
    const videoUrl = videoSourceType === 'crm' ? formData.crm_video_url : formData.video_url;
    if (!videoUrl && !formData.video_file) {
      toast.error('Please provide a video URL or upload a video');
      return;
    }

    setPublishing(true);
    try {
      // Create the cinematic ad content
      const payload = {
        content_type: 'cinematic_ad',
        title: formData.title,
        description: formData.description || formData.ad_copy,
        content: JSON.stringify({
          headline: formData.headline,
          ad_copy: formData.ad_copy,
          cta_text: formData.cta_text,
          cta_url: formData.cta_url,
          landing_page_url: formData.landing_page_url,
          automation_type: formData.automation_type,
          deployment_platforms: selectedPlatforms,
          ad_components: adComponents,
        }),
        video_url: videoSourceType === 'crm' ? formData.crm_video_url : formData.video_url,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : ['cinematic', 'video-ad'],
        contact_email: formData.contact_email || user?.email,
        external_url: formData.landing_page_url || formData.cta_url,
        is_featured: true,
      };

      await api.post('/api/directory/content', payload);
      
      // Create orders for selected managed services platforms
      const managedPlatforms = selectedPlatforms.filter(p => p !== 'directory');
      if (managedPlatforms.length > 0) {
        toast.success(`Cinematic Ad published! Creating orders for ${managedPlatforms.length} platform(s)...`);
        // Navigate to orders or create orders for each platform
      } else {
        toast.success('Cinematic Ad published to Best of Best Directory!');
      }
      
      setShowCinematicModal(false);
      resetFormData();
      loadData(); // This will also refresh credits
    } catch (error) {
      console.error('Publish error:', error);
      // Handle insufficient credits error
      if (error.response?.status === 402) {
        toast.error(error.response.data?.detail || 'Insufficient publishing credits');
      } else {
        toast.error('Failed to publish cinematic ad');
      }
    } finally {
      setPublishing(false);
    }
  };

  const resetFormData = () => {
    setFormData({
      title: '', description: '', content: '', category: '', tags: '',
      images: [], image_url: '', video_url: '', video_file: null, crm_video_url: '',
      soundcloud_url: '', price: '', location: '',
      event_date: '', event_time: '', contact_email: '', external_url: '', is_featured: false,
      promote_to_directory: false,
      headline: '', ad_copy: '', cta_text: '', cta_url: '', landing_page_url: '', automation_type: '',
    });
    setSelectedPlatforms(['directory']);
    setAdComponents({ video: false, image: false, headline: false, ad_copy: false, cta: false, landing_page: false, automations: false });
    setVideoSourceType('url');
  };

  // Content management functions
  const handleTogglePromote = async (content) => {
    setActionLoading(content.id);
    try {
      const newValue = !content.promote_to_directory;
      await api.put(`/api/directory/content/${content.id}`, {
        ...content,
        tags: content.tags || [],
        promote_to_directory: newValue
      });
      toast.success(newValue ? 'Content promoted to directory!' : 'Content removed from directory promotion');
      loadData();
    } catch (error) {
      toast.error('Failed to update promotion status');
    } finally {
      setActionLoading(null);
      setActiveDropdown(null);
    }
  };

  const handleDeleteContent = async (contentId) => {
    setActionLoading(contentId);
    try {
      await api.delete(`/api/directory/content/${contentId}`);
      toast.success('Content deleted successfully');
      setShowDeleteConfirm(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete content');
    } finally {
      setActionLoading(null);
    }
  };

  const handleEditContent = (content) => {
    setEditingContent(content);
    setFormData({
      title: content.title || '',
      description: content.description || '',
      content: content.content || '',
      category: content.category || '',
      tags: (content.tags || []).join(', '),
      images: content.images || [],
      video_url: content.video_url || '',
      video_file: null,
      crm_video_url: '',
      soundcloud_url: content.soundcloud_url || '',
      price: content.price?.toString() || '',
      location: content.location || '',
      event_date: content.event_date || '',
      event_time: content.event_time || '',
      contact_email: content.contact_email || '',
      external_url: content.external_url || '',
      is_featured: content.is_featured || false,
      promote_to_directory: content.promote_to_directory || false,
      headline: '', ad_copy: '', cta_text: '', cta_url: '', landing_page_url: '', automation_type: '',
    });
    setShowEditModal(true);
    setActiveDropdown(null);
  };

  // Publish content to AI Business Finder Directory
  const handlePublishToExternalDirectory = async (content) => {
    setActionLoading(content.id);
    try {
      await api.publishContentToDirectory({ 
        content_ids: [content.id],
        category: content.category || content.content_type
      });
      toast.success('Content published to AI Business Finder!');
      // Update local state to show published status
      setDirectoryPublishStatus(prev => ({
        ...prev,
        [content.id]: { status: 'published', published_at: new Date().toISOString() }
      }));
      setActiveDropdown(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish to directory');
    } finally {
      setActionLoading(null);
    }
  };

  // Unpublish content from AI Business Finder Directory
  const handleUnpublishFromExternalDirectory = async (content) => {
    setActionLoading(content.id);
    try {
      await api.unpublishContentFromDirectory(content.id);
      toast.success('Content unpublished from AI Business Finder');
      setDirectoryPublishStatus(prev => ({
        ...prev,
        [content.id]: { status: 'draft' }
      }));
      setActiveDropdown(null);
    } catch (error) {
      toast.error('Failed to unpublish');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingContent) return;
    
    setPublishing(true);
    try {
      await api.put(`/api/directory/content/${editingContent.id}`, {
        content_type: editingContent.content_type,
        title: formData.title,
        description: formData.description,
        content: formData.content,
        category: formData.category,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
        images: formData.images,
        video_url: formData.video_url || null,
        soundcloud_url: formData.soundcloud_url || null,
        price: formData.price ? parseFloat(formData.price) : null,
        location: formData.location || null,
        event_date: formData.event_date || null,
        event_time: formData.event_time || null,
        contact_email: formData.contact_email || null,
        external_url: formData.external_url || null,
        is_featured: formData.is_featured,
        promote_to_directory: formData.promote_to_directory,
      });
      toast.success('Content updated successfully!');
      setShowEditModal(false);
      setEditingContent(null);
      resetFormData();
      loadData();
    } catch (error) {
      toast.error('Failed to update content');
    } finally {
      setPublishing(false);
    }
  };

  // Get filtered content
  const filteredContent = contentFilter === 'all' 
    ? myContent 
    : contentFilter === 'promoted'
    ? myContent.filter(c => c.promote_to_directory)
    : myContent.filter(c => c.content_type === contentFilter);

  // Get unique content types from user's content
  const myContentTypes = [...new Set(myContent.map(c => c.content_type))];

  const togglePlatform = (platformId) => {
    if (platformId === 'directory') return; // Always required
    setSelectedPlatforms(prev => 
      prev.includes(platformId) 
        ? prev.filter(p => p !== platformId)
        : [...prev, platformId]
    );
  };

  const toggleAdComponent = (componentId) => {
    setAdComponents(prev => ({
      ...prev,
      [componentId]: !prev[componentId]
    }));
  };

  const getIcon = (type) => {
    const Icon = CONTENT_ICONS[type] || FileText;
    return Icon;
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-8 pt-20">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-cyan-500 flex items-center justify-center">
              <Send className="w-7 h-7 text-black" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-white">Publish Content</h1>
              <p className="text-gray-400">Share content that streams to the AI Horse-Powered Directory</p>
            </div>
          </div>
          
          {/* Stats */}
          <div className="flex flex-wrap gap-4 mt-6">
            {/* Publishing Credits Card - Prominent */}
            <div className={cn(
              "glass rounded-xl px-5 py-3 border",
              credits.publishing_credits < 25 ? "border-red-500/50 bg-red-500/5" : "border-primary/50 bg-primary/5"
            )}>
              <div className="flex items-center gap-2">
                <Sparkles className={cn("w-4 h-4", credits.publishing_credits < 25 ? "text-red-400" : "text-primary")} />
                <p className={cn("text-2xl font-bold", credits.publishing_credits < 25 ? "text-red-400" : "text-primary")}>
                  {credits.publishing_credits?.toLocaleString() || 0}
                </p>
              </div>
              <p className="text-xs text-gray-400">Publishing Credits</p>
              <p className="text-[10px] text-gray-500 mt-1">{credits.credit_cost_per_publish} credits per post</p>
            </div>
            <div className="glass rounded-xl px-5 py-3">
              <p className="text-2xl font-bold text-white">{myContent.length}</p>
              <p className="text-xs text-gray-400">Published</p>
            </div>
            <div className="glass rounded-xl px-5 py-3">
              <p className="text-2xl font-bold text-cyan-400">{myContent.reduce((sum, c) => sum + (c.views || 0), 0)}</p>
              <p className="text-xs text-gray-400">Total Views</p>
            </div>
            <div className="glass rounded-xl px-5 py-3">
              <p className="text-2xl font-bold text-pink-400">{myContent.reduce((sum, c) => sum + (c.likes || 0), 0)}</p>
              <p className="text-xs text-gray-400">Total Likes</p>
            </div>
          </div>

          {/* Low credits warning */}
          {credits.publishing_credits < 25 && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-400" />
                <div>
                  <p className="text-sm font-medium text-red-400">Insufficient Publishing Credits</p>
                  <p className="text-xs text-gray-400">You need at least 25 credits to publish content</p>
                </div>
              </div>
              <Link to="/pricing">
                <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white">
                  Upgrade to get more credits
                </Button>
              </Link>
            </motion.div>
          )}
        </motion.div>

        {/* Cinematic Video Ad Deployment - Featured Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mb-8"
        >
          <div className="glass rounded-2xl border-2 border-cyan-500/40 bg-gradient-to-br from-cyan-900/30 via-purple-900/20 to-pink-900/20 overflow-hidden relative">
            {/* Animated background glow */}
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-transparent to-purple-500/10 animate-pulse" style={{ animationDuration: '4s' }} />
            
            <div className="relative z-10 p-6 lg:p-8">
              <div className="flex flex-col lg:flex-row items-start gap-6">
                {/* Left: Icon and Title */}
                <div className="flex items-start gap-4 flex-1">
                  <motion.div 
                    className="w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center shrink-0 shadow-lg shadow-cyan-500/30"
                    animate={{ 
                      boxShadow: ['0 10px 40px rgba(0,255,255,0.3)', '0 10px 60px rgba(0,255,255,0.5)', '0 10px 40px rgba(0,255,255,0.3)']
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <Clapperboard className="w-10 h-10 text-white" />
                  </motion.div>
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white">Cinematic Video Ad Deployment</h2>
                      <span className="text-xs px-3 py-1 rounded-full bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-semibold animate-pulse">FEATURED</span>
                    </div>
                    <p className="text-gray-300 mb-4 max-w-2xl">
                      Deploy your complete <strong className="text-cyan-400">Cinematic Video Ad</strong> with headline, ad copy, CTA, landing page, and automations. 
                      Publish to the <strong className="text-accent">Best of Best Directory</strong> first, then cascade to Meta, Google, TikTok, and beyond.
                    </p>
                    
                    {/* Ad Components Checklist */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {AD_COMPONENTS.map((comp) => {
                        const Icon = comp.icon;
                        return (
                          <div key={comp.id} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10">
                            <Icon className="w-4 h-4 text-cyan-400" />
                            <span className="text-xs text-gray-300">{comp.name}</span>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Deployment Flow */}
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="text-gray-400">Deploy to:</span>
                      <span className="px-2 py-1 rounded-lg bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 font-medium">Directory</span>
                      <ArrowRight className="w-4 h-4 text-gray-500" />
                      <span className="px-2 py-1 rounded-lg bg-blue-500/20 text-blue-400">Meta</span>
                      <span className="px-2 py-1 rounded-lg bg-red-500/20 text-red-400">Google</span>
                      <span className="px-2 py-1 rounded-lg bg-pink-500/20 text-pink-400">TikTok</span>
                      <span className="text-gray-500">+ more</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: CTA Buttons */}
                <div className="flex flex-col gap-3 shrink-0 w-full lg:w-auto">
                  <Button 
                    onClick={() => setShowCinematicModal(true)}
                    className="btn-primary bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white font-bold px-8 py-4 text-lg shadow-lg shadow-cyan-500/30"
                    data-testid="deploy-cinematic-ad-btn"
                  >
                    <Film className="w-5 h-5 mr-2" />
                    Deploy Cinematic Ad
                  </Button>
                  <Link to="/cinematic-ads" className="w-full">
                    <Button variant="outline" className="w-full border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Open Cinematic Studio
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Best of Best Content Types */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-4">
            <Award className="w-6 h-6 text-amber-400" />
            <h2 className="font-heading text-xl font-semibold text-white">Best of Best Advertising</h2>
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">PREMIUM</span>
          </div>
          <p className="text-gray-400 text-sm mb-4">
            Expand your reach beyond digital. Publish to TV, Radio, Billboards, Direct Mail, and Affiliate Networks.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {BEST_OF_BEST_TYPES.map((type, i) => {
              const Icon = type.icon;
              const gradient = CONTENT_COLORS[type.type];
              return (
                <motion.button
                  key={type.type}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => handleSelectType({ type: type.type, name: type.name, description: type.description })}
                  className="glass rounded-2xl p-4 text-left hover:border-amber-500/50 transition-all group relative overflow-hidden border border-amber-500/20"
                  data-testid={`publish-type-${type.type}`}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-10 transition-opacity`} />
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-2 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-white text-sm mb-0.5 group-hover:text-amber-400 transition-colors">
                    {type.name}
                  </h3>
                  <p className="text-xs text-gray-500 line-clamp-2">{type.description}</p>
                  <Plus className="w-4 h-4 text-gray-600 absolute top-3 right-3 group-hover:text-amber-400 transition-colors" />
                </motion.button>
              );
            })}
          </div>
        </motion.div>

        {/* Inventory Listings Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.09 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-4">
            <Package className="w-6 h-6 text-cyan-400" />
            <h2 className="font-heading text-xl font-semibold text-white">Inventory Listings</h2>
            <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">SINGLE OR BULK</span>
          </div>
          <p className="text-gray-400 text-sm mb-4">
            Upload your inventory (Real Estate, Vehicles, RVs, Furniture) as single listings or bulk CSV imports. Each item becomes a searchable directory page.
          </p>
          <div className="bg-[#0a0f1a] rounded-2xl border border-white/10 p-6">
            <InventoryListings />
          </div>
        </motion.div>

        {/* Content Types Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-10"
        >
          <h2 className="font-heading text-xl font-semibold text-white mb-4">Choose Content Type</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {contentTypes.map((type, i) => {
              const Icon = getIcon(type.type);
              const gradient = CONTENT_COLORS[type.type] || 'from-gray-500 to-gray-600';
              return (
                <motion.button
                  key={type.type}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => handleSelectType(type)}
                  className="glass rounded-2xl p-5 text-left hover:border-primary/50 transition-all group relative overflow-hidden"
                  data-testid={`publish-type-${type.type}`}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-10 transition-opacity`} />
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-white text-sm mb-1 group-hover:text-primary transition-colors">
                    {type.name}
                  </h3>
                  <p className="text-xs text-gray-500 line-clamp-2">{type.description}</p>
                  <Plus className="w-4 h-4 text-gray-600 absolute top-4 right-4 group-hover:text-primary transition-colors" />
                </motion.button>
              );
            })}
          </div>
        </motion.div>

        {/* My Published Content - Enhanced Dashboard */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <h2 className="font-heading text-xl font-semibold text-white">My Published Content</h2>
            <div className="flex items-center gap-2">
              {/* Filter Dropdown */}
              <div className="flex items-center gap-2 bg-white/5 rounded-lg p-1">
                <button
                  onClick={() => setContentFilter('all')}
                  className={cn(
                    "px-3 py-1.5 rounded text-xs font-medium transition-all",
                    contentFilter === 'all' ? "bg-primary text-white" : "text-gray-400 hover:text-white"
                  )}
                >
                  All ({myContent.length})
                </button>
                <button
                  onClick={() => setContentFilter('promoted')}
                  className={cn(
                    "px-3 py-1.5 rounded text-xs font-medium transition-all flex items-center gap-1",
                    contentFilter === 'promoted' ? "bg-purple-500 text-white" : "text-gray-400 hover:text-white"
                  )}
                >
                  <Megaphone className="w-3 h-3" />
                  Promoted ({myContent.filter(c => c.promote_to_directory).length})
                </button>
              </div>
              <a href={EXTERNAL_DIRECTORY_URL} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">
                View Directory <ArrowRight className="inline w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Content Type Quick Filters */}
          {myContentTypes.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {myContentTypes.map(type => {
                const Icon = getIcon(type);
                return (
                  <button
                    key={type}
                    onClick={() => setContentFilter(contentFilter === type ? 'all' : type)}
                    className={cn(
                      "px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5",
                      contentFilter === type 
                        ? "bg-primary/20 text-primary border border-primary/30" 
                        : "bg-white/5 text-gray-400 hover:text-white border border-white/10"
                    )}
                  >
                    <Icon className="w-3 h-3" />
                    {type.replace('_', ' ')}
                  </button>
                );
              })}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : myContent.length === 0 ? (
            <div className="glass rounded-2xl p-12 text-center">
              <Globe className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Content Yet</h3>
              <p className="text-gray-400 mb-4">Start publishing to the directory and reach thousands of potential customers!</p>
            </div>
          ) : filteredContent.length === 0 ? (
            <div className="glass rounded-2xl p-8 text-center">
              <Filter className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">No content matches this filter</p>
              <button onClick={() => setContentFilter('all')} className="text-primary text-sm hover:underline mt-2">
                Show all content
              </button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredContent.map((content) => {
                const Icon = getIcon(content.content_type);
                const gradient = CONTENT_COLORS[content.content_type] || 'from-gray-500 to-gray-600';
                const isPromoted = content.promote_to_directory;
                return (
                  <motion.div
                    key={content.id}
                    whileHover={{ y: -5 }}
                    className={cn(
                      "glass rounded-2xl p-5 relative overflow-hidden",
                      isPromoted && "ring-2 ring-purple-500/30"
                    )}
                    data-testid={`content-card-${content.id}`}
                  >
                    <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br ${gradient} opacity-10 rounded-bl-full`} />
                    
                    {/* Promoted Badge */}
                    {isPromoted && (
                      <div className="absolute top-3 left-3 px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 text-[10px] font-medium flex items-center gap-1">
                        <Megaphone className="w-3 h-3" />
                        Promoted
                      </div>
                    )}
                    
                    {/* Actions Menu */}
                    <div className="absolute top-3 right-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveDropdown(activeDropdown === content.id ? null : content.id);
                        }}
                        className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      
                      {/* Dropdown Menu */}
                      {activeDropdown === content.id && (
                        <div className="absolute right-0 top-8 w-44 bg-card border border-white/10 rounded-lg shadow-xl z-10 py-1">
                          <button
                            onClick={() => handleEditContent(content)}
                            className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-white/5 flex items-center gap-2"
                          >
                            <Edit className="w-4 h-4" />
                            Edit Content
                          </button>
                          <button
                            onClick={() => handleTogglePromote(content)}
                            disabled={actionLoading === content.id}
                            className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-white/5 flex items-center gap-2"
                          >
                            {actionLoading === content.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : isPromoted ? (
                              <MegaphoneOff className="w-4 h-4" />
                            ) : (
                              <Megaphone className="w-4 h-4" />
                            )}
                            {isPromoted ? 'Remove Promotion' : 'Promote to Directory'}
                          </button>
                          <hr className="my-1 border-white/10" />
                          {/* Publish to AI Business Finder */}
                          {(directoryPublishStatus[content.id]?.status === 'published' || content.directory_publish_status?.status === 'published') ? (
                            <button
                              onClick={() => handleUnpublishFromExternalDirectory(content)}
                              disabled={actionLoading === content.id}
                              className="w-full px-3 py-2 text-left text-sm text-amber-400 hover:bg-amber-500/10 flex items-center gap-2"
                            >
                              {actionLoading === content.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Globe className="w-4 h-4" />
                              )}
                              Unpublish from AI Business Finder
                            </button>
                          ) : (
                            <button
                              onClick={() => handlePublishToExternalDirectory(content)}
                              disabled={actionLoading === content.id}
                              className="w-full px-3 py-2 text-left text-sm text-amber-400 hover:bg-amber-500/10 flex items-center gap-2"
                            >
                              {actionLoading === content.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Globe className="w-4 h-4" />
                              )}
                              Publish to AI Business Finder
                            </button>
                          )}
                          <Link
                            to={`/directory/content/${content.id}`}
                            className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-white/5 flex items-center gap-2"
                          >
                            <ExternalLink className="w-4 h-4" />
                            View in Directory
                          </Link>
                          <hr className="my-1 border-white/10" />
                          <button
                            onClick={() => {
                              setShowDeleteConfirm(content.id);
                              setActiveDropdown(null);
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-start gap-3 mb-3 mt-6">
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-white truncate">{content.title}</h3>
                        <p className="text-xs text-gray-500 capitalize">{content.content_type.replace('_', ' ')}</p>
                      </div>
                    </div>
                    
                    {/* External Directory Badge */}
                    {(directoryPublishStatus[content.id]?.status === 'published' || content.directory_publish_status?.status === 'published') && (
                      <div className="mb-2 px-2 py-1 rounded bg-amber-500/20 text-amber-400 text-[10px] font-medium inline-flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        Live on AI Business Finder
                      </div>
                    )}
                    
                    <p className="text-sm text-gray-400 line-clamp-2 mb-4">{content.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {content.views || 0}</span>
                        <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {content.likes || 0}</span>
                      </div>
                      <span className="text-xs text-gray-600">{new Date(content.created_at).toLocaleDateString()}</span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>
      </div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-card border border-white/10 rounded-2xl p-6 max-w-sm w-full"
            >
              <div className="text-center mb-4">
                <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-3">
                  <Trash2 className="w-6 h-6 text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Delete Content?</h3>
                <p className="text-sm text-gray-400">This action cannot be undone. The content will be permanently removed from the directory.</p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 border-white/20"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleDeleteContent(showDeleteConfirm)}
                  disabled={actionLoading === showDeleteConfirm}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                >
                  {actionLoading === showDeleteConfirm ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Delete'
                  )}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Edit Content Modal */}
      <AnimatePresence>
        {showEditModal && editingContent && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 pt-24 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-card border border-white/10 rounded-2xl p-6 max-w-lg w-full mb-8 mt-4"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  {(() => {
                    const Icon = getIcon(editingContent.content_type);
                    const gradient = CONTENT_COLORS[editingContent.content_type];
                    return (
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                    );
                  })()}
                  <div>
                    <h2 className="font-heading text-lg font-bold text-white">Edit Content</h2>
                    <p className="text-xs text-gray-400 capitalize">{editingContent.content_type.replace('_', ' ')}</p>
                  </div>
                </div>
                <button onClick={() => { setShowEditModal(false); setEditingContent(null); resetFormData(); }} className="text-gray-400 hover:text-white p-1">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSaveEdit} className="space-y-3">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Title *</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Enter a compelling title"
                    className="bg-black/30 border-white/10 h-9"
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Description *</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description"
                    rows={2}
                    className="bg-black/30 border-white/10"
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Content</label>
                  <Textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Main content"
                    rows={4}
                    className="bg-black/30 border-white/10"
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Tags (comma separated)</label>
                  <Input
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="tag1, tag2, tag3"
                    className="bg-black/30 border-white/10 h-9"
                  />
                </div>

                {/* Promote to Directory Toggle */}
                <div className={cn(
                  "flex items-center justify-between p-3 rounded-lg border transition-all",
                  formData.promote_to_directory 
                    ? "bg-purple-500/20 border-purple-500/40" 
                    : "bg-gray-800/50 border-gray-600/30"
                )}>
                  <div className="flex items-center gap-2">
                    <Megaphone className={cn(
                      "w-4 h-4 transition-colors",
                      formData.promote_to_directory ? "text-purple-400" : "text-gray-500"
                    )} />
                    <div>
                      <span className="text-sm text-white">Promote to Directory</span>
                      <p className="text-xs text-gray-500">Shows on directory homepage</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, promote_to_directory: !formData.promote_to_directory })}
                    className={cn(
                      "w-12 h-6 rounded-full transition-all duration-200 relative",
                      formData.promote_to_directory 
                        ? "bg-purple-500 shadow-lg shadow-purple-500/30" 
                        : "bg-gray-600"
                    )}
                  >
                    <span className={cn(
                      "absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all duration-200",
                      formData.promote_to_directory ? "left-7" : "left-1"
                    )} />
                  </button>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setShowEditModal(false); setEditingContent(null); resetFormData(); }}
                    className="flex-1 border-white/20"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={publishing}
                    className="flex-1 bg-gradient-to-r from-primary to-cyan-400 hover:from-primary/90 hover:to-cyan-400/90"
                  >
                    {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Publish Modal - Fixed and scrollable */}
      <AnimatePresence>
        {showPublishModal && selectedType && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 pt-24 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-card border border-white/10 rounded-2xl p-6 max-w-lg w-full mb-8 mt-4"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  {(() => {
                    const Icon = getIcon(selectedType.type);
                    const gradient = CONTENT_COLORS[selectedType.type];
                    return (
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                    );
                  })()}
                  <div>
                    <h2 className="font-heading text-lg font-bold text-white">Publish {selectedType.name}</h2>
                    <p className="text-xs text-gray-400">{selectedType.description}</p>
                  </div>
                </div>
                <button onClick={() => setShowPublishModal(false)} className="text-gray-400 hover:text-white p-1">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handlePublish} className="space-y-3">
                {/* Title */}
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Title *</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Enter a compelling title"
                    className="bg-black/30 border-white/10 h-9"
                    data-testid="publish-title"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Description *</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description"
                    rows={2}
                    className="bg-black/30 border-white/10 text-sm"
                    data-testid="publish-description"
                  />
                </div>

                {/* Content (for articles/discussions) */}
                {['discussion', 'community_article', 'blog_article'].includes(selectedType.type) && (
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Content</label>
                    <Textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      placeholder="Full content..."
                      rows={4}
                      className="bg-black/30 border-white/10 text-sm"
                    />
                  </div>
                )}

                {/* Universal Image/Video Upload Section - For all content types except video (which has its own) */}
                {selectedType.type !== 'video' && (
                  <div className="space-y-3 p-4 bg-white/5 rounded-xl border border-white/10">
                    <div className="flex items-center justify-between">
                      <label className="text-xs text-gray-400 flex items-center gap-2">
                        <Image className="w-4 h-4" />
                        Media {formData.promote_to_directory ? <span className="text-amber-400">(Required)</span> : '(Optional)'}
                      </label>
                    </div>
                    
                    {/* Image & Video Drag-Drop Grid */}
                    <div className="grid sm:grid-cols-2 gap-3">
                      {/* Image Upload */}
                      <div>
                        <label className="text-[10px] text-gray-500 mb-1 block">
                          Image {formData.promote_to_directory && !formData.video_url && <span className="text-amber-400">*</span>}
                        </label>
                        <DragDropUpload
                          type="image"
                          currentFile={formData.image_url || formData.images?.[0]}
                          onUpload={(url) => setFormData({ ...formData, image_url: url, images: [url] })}
                          onRemove={() => setFormData({ ...formData, image_url: '', images: [] })}
                          compact
                        />
                        <Input
                          value={formData.image_url || formData.images?.[0] || ''}
                          onChange={(e) => setFormData({ ...formData, image_url: e.target.value, images: e.target.value ? [e.target.value] : [] })}
                          placeholder="Or paste image URL..."
                          className="bg-black/30 border-white/10 h-7 text-xs mt-2"
                        />
                      </div>
                      
                      {/* Video Upload */}
                      <div>
                        <label className="text-[10px] text-gray-500 mb-1 block">
                          Video {formData.promote_to_directory && !formData.image_url && !formData.images?.length && <span className="text-amber-400">*</span>}
                        </label>
                        <DragDropUpload
                          type="video"
                          currentFile={formData.video_url}
                          onUpload={(url) => setFormData({ ...formData, video_url: url })}
                          onRemove={() => setFormData({ ...formData, video_url: '' })}
                          compact
                        />
                        <Input
                          value={formData.video_url || ''}
                          onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                          placeholder="Or paste video URL..."
                          className="bg-black/30 border-white/10 h-7 text-xs mt-2"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Video URL - Enhanced with multiple source options */}
                {selectedType.type === 'video' && (
                  <div className="space-y-3">
                    <label className="text-xs text-gray-400 mb-1 block">Video Source</label>
                    
                    {/* Source Type Selector */}
                    <div className="flex gap-2 mb-2">
                      <button
                        type="button"
                        onClick={() => setVideoSourceType('url')}
                        className={cn(
                          "flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-2",
                          videoSourceType === 'url' 
                            ? "bg-primary/20 text-primary border border-primary/30" 
                            : "bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10"
                        )}
                      >
                        <Link2 className="w-3 h-3" />
                        URL (YouTube/Vimeo)
                      </button>
                      <button
                        type="button"
                        onClick={() => setVideoSourceType('crm')}
                        className={cn(
                          "flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-2",
                          videoSourceType === 'crm' 
                            ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30" 
                            : "bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10"
                        )}
                      >
                        <Film className="w-3 h-3" />
                        CRM Media Link
                      </button>
                      <button
                        type="button"
                        onClick={() => setVideoSourceType('upload')}
                        className={cn(
                          "flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-2",
                          videoSourceType === 'upload' 
                            ? "bg-purple-500/20 text-purple-400 border border-purple-500/30" 
                            : "bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10"
                        )}
                      >
                        <Upload className="w-3 h-3" />
                        Upload
                      </button>
                    </div>
                    
                    {/* URL Input */}
                    {videoSourceType === 'url' && (
                      <Input
                        value={formData.video_url}
                        onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                        placeholder="YouTube, Vimeo, or direct video URL"
                        className="bg-black/30 border-white/10 h-9"
                      />
                    )}
                    
                    {/* CRM Media Link Input */}
                    {videoSourceType === 'crm' && (
                      <div>
                        <Input
                          value={formData.crm_video_url}
                          onChange={(e) => setFormData({ ...formData, crm_video_url: e.target.value })}
                          placeholder="Paste your CRM media storage link..."
                          className="bg-black/30 border-cyan-500/20 h-9"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Use your CRM media storage URL (e.g., https://storage.googleapis.com/...)
                        </p>
                      </div>
                    )}
                    
                    {/* Upload Input */}
                    {videoSourceType === 'upload' && (
                      <div className="border-2 border-dashed border-white/10 rounded-lg p-4 text-center hover:border-primary/30 transition-colors cursor-pointer"
                        onClick={() => videoInputRef.current?.click()}
                      >
                        <input
                          ref={videoInputRef}
                          type="file"
                          accept="video/*"
                          className="hidden"
                          onChange={(e) => setFormData({ ...formData, video_file: e.target.files?.[0] || null })}
                        />
                        {formData.video_file ? (
                          <div className="flex items-center justify-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-400" />
                            <span className="text-sm text-white">{formData.video_file.name}</span>
                          </div>
                        ) : (
                          <>
                            <Upload className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                            <p className="text-sm text-gray-400">Click to upload video</p>
                            <p className="text-xs text-gray-500">MP4, MOV, WebM (max 500MB)</p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* SoundCloud URL */}
                {selectedType.type === 'soundcloud_post' && (
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">SoundCloud URL</label>
                    <Input
                      value={formData.soundcloud_url}
                      onChange={(e) => setFormData({ ...formData, soundcloud_url: e.target.value })}
                      placeholder="SoundCloud track or playlist URL"
                      className="bg-black/30 border-white/10 h-9"
                    />
                  </div>
                )}

                {/* Price (for products/properties) */}
                {['product', 'digital_product', 'property', 'classified'].includes(selectedType.type) && (
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Price ($)</label>
                    <Input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      placeholder="0.00"
                      className="bg-black/30 border-white/10 h-9"
                    />
                  </div>
                )}

                {/* Location */}
                {['event', 'property', 'job_listing', 'classified'].includes(selectedType.type) && (
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Location</label>
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="City, State"
                      className="bg-black/30 border-white/10 h-9"
                    />
                  </div>
                )}

                {/* Event Date/Time */}
                {selectedType.type === 'event' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Event Date</label>
                      <Input
                        type="date"
                        value={formData.event_date}
                        onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                        className="bg-black/30 border-white/10 h-9"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Event Time</label>
                      <Input
                        type="time"
                        value={formData.event_time}
                        onChange={(e) => setFormData({ ...formData, event_time: e.target.value })}
                        className="bg-black/30 border-white/10 h-9"
                      />
                    </div>
                  </div>
                )}

                {/* Tags */}
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Tags (comma-separated)</label>
                  <Input
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="marketing, ai, business"
                    className="bg-black/30 border-white/10 h-9"
                  />
                </div>

                {/* Contact Email */}
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Contact Email</label>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="your@email.com"
                    className="bg-black/30 border-white/10 h-9"
                  />
                </div>

                {/* External URL */}
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">External Link</label>
                  <Input
                    value={formData.external_url}
                    onChange={(e) => setFormData({ ...formData, external_url: e.target.value })}
                    placeholder="https://..."
                    className="bg-black/30 border-white/10 h-9"
                  />
                </div>

                {/* Promote to Directory Toggle */}
                <div className={cn(
                  "flex items-center justify-between p-3 rounded-lg border transition-all",
                  formData.promote_to_directory 
                    ? "bg-purple-500/20 border-purple-500/40" 
                    : "bg-gray-800/50 border-gray-600/30"
                )}>
                  <div className="flex items-center gap-2">
                    <Megaphone className={cn(
                      "w-4 h-4 transition-colors",
                      formData.promote_to_directory ? "text-purple-400" : "text-gray-500"
                    )} />
                    <div>
                      <span className="text-sm text-white">Promote to Directory</span>
                      <p className="text-xs text-gray-500">Show on directory homepage</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, promote_to_directory: !formData.promote_to_directory })}
                    className={cn(
                      "w-12 h-6 rounded-full transition-all duration-200 relative",
                      formData.promote_to_directory 
                        ? "bg-purple-500 shadow-lg shadow-purple-500/30" 
                        : "bg-gray-600"
                    )}
                  >
                    <span className={cn(
                      "absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all duration-200",
                      formData.promote_to_directory ? "left-7" : "left-1"
                    )} />
                  </button>
                </div>

                {/* Submit */}
                <div className="flex gap-3 pt-3">
                  <Button 
                    type="submit" 
                    className="flex-1 btn-primary h-9"
                    disabled={publishing}
                    data-testid="publish-submit"
                  >
                    {publishing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" /> Publish
                      </>
                    )}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowPublishModal(false)}
                    className="border-white/20 h-9"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Cinematic Ad Deployment Modal */}
      <AnimatePresence>
        {showCinematicModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center p-4 pt-20 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-card border border-cyan-500/30 rounded-2xl max-w-2xl w-full mb-8 overflow-hidden"
            >
              {/* Modal Header - Compact */}
              <div className="bg-gradient-to-r from-cyan-900/50 to-purple-900/50 p-4 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center">
                      <Clapperboard className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="font-heading text-lg font-bold text-white">Deploy Cinematic Video Ad</h2>
                      <p className="text-xs text-gray-400">Complete your ad package and deploy to the directory</p>
                    </div>
                  </div>
                  <button onClick={() => setShowCinematicModal(false)} className="text-gray-400 hover:text-white p-1">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleCinematicPublish} className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Campaign Title */}
                <div>
                  <label className="text-xs font-medium text-white mb-1 block">Campaign Title *</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Summer Sale Video Campaign"
                    className="bg-black/30 border-white/10 h-9"
                    data-testid="cinematic-title"
                  />
                </div>

                {/* Video Source Section */}
                <div className="glass rounded-xl p-3">
                  <label className="text-xs font-medium text-white mb-2 flex items-center gap-2">
                    <Film className="w-3 h-3 text-cyan-400" />
                    Video Source *
                  </label>
                  
                  {/* Source Type Tabs */}
                  <div className="flex gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => setVideoSourceType('crm')}
                      className={cn(
                        "flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-1",
                        videoSourceType === 'crm' 
                          ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/50" 
                          : "bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10"
                      )}
                    >
                      <Film className="w-3 h-3" />
                      CRM Media
                    </button>
                    <button
                      type="button"
                      onClick={() => setVideoSourceType('url')}
                      className={cn(
                        "flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-1",
                        videoSourceType === 'url' 
                          ? "bg-primary/20 text-primary border border-primary/50" 
                          : "bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10"
                      )}
                    >
                      <Link2 className="w-3 h-3" />
                      Direct URL
                    </button>
                    <button
                      type="button"
                      onClick={() => setVideoSourceType('upload')}
                      className={cn(
                        "flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-1",
                        videoSourceType === 'upload' 
                          ? "bg-purple-500/20 text-purple-400 border border-purple-500/50" 
                          : "bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10"
                      )}
                    >
                      <Upload className="w-3 h-3" />
                      Upload
                    </button>
                  </div>
                  
                  {/* CRM Media Input */}
                  {videoSourceType === 'crm' && (
                    <div>
                      <Input
                        value={formData.crm_video_url}
                        onChange={(e) => setFormData({ ...formData, crm_video_url: e.target.value })}
                        placeholder="Paste your CRM media storage link..."
                        className="bg-black/30 border-cyan-500/20 h-9"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Use your CRM media storage URL from your media library
                      </p>
                    </div>
                  )}
                  
                  {/* Direct URL Input */}
                  {videoSourceType === 'url' && (
                    <Input
                      value={formData.video_url}
                      onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                      placeholder="YouTube, Vimeo, or any direct video URL"
                      className="bg-black/30 border-white/10 h-11"
                    />
                  )}
                  
                  {/* Upload Input */}
                  {videoSourceType === 'upload' && (
                    <div 
                      className="border-2 border-dashed border-purple-500/30 rounded-xl p-6 text-center hover:border-purple-500/50 transition-colors cursor-pointer bg-purple-500/5"
                      onClick={() => videoInputRef.current?.click()}
                    >
                      <input
                        ref={videoInputRef}
                        type="file"
                        accept="video/*"
                        className="hidden"
                        onChange={(e) => setFormData({ ...formData, video_file: e.target.files?.[0] || null })}
                      />
                      {formData.video_file ? (
                        <div className="flex items-center justify-center gap-3">
                          <CheckCircle className="w-6 h-6 text-green-400" />
                          <span className="text-white font-medium">{formData.video_file.name}</span>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-10 h-10 text-purple-400 mx-auto mb-3" />
                          <p className="text-white font-medium">Click to upload your video</p>
                          <p className="text-xs text-gray-500 mt-1">MP4, MOV, WebM supported (max 500MB)</p>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Ad Components Grid */}
                <div>
                  <label className="text-sm font-medium text-white mb-3 block">Ad Components</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {AD_COMPONENTS.map((comp) => {
                      const Icon = comp.icon;
                      const isChecked = adComponents[comp.id];
                      return (
                        <button
                          key={comp.id}
                          type="button"
                          onClick={() => toggleAdComponent(comp.id)}
                          className={cn(
                            "p-3 rounded-xl text-left transition-all flex items-start gap-3",
                            isChecked 
                              ? "bg-cyan-500/20 border-2 border-cyan-500/50" 
                              : "bg-white/5 border border-white/10 hover:bg-white/10"
                          )}
                        >
                          <div className={cn(
                            "w-8 h-8 rounded-lg flex items-center justify-center shrink-0",
                            isChecked ? "bg-cyan-500/30" : "bg-white/10"
                          )}>
                            <Icon className={cn("w-4 h-4", isChecked ? "text-cyan-400" : "text-gray-400")} />
                          </div>
                          <div>
                            <p className={cn("text-sm font-medium", isChecked ? "text-white" : "text-gray-300")}>{comp.name}</p>
                            <p className="text-xs text-gray-500">{comp.description}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Headline & Ad Copy */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-white mb-2 block">Headline</label>
                    <Input
                      value={formData.headline}
                      onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                      placeholder="Your attention-grabbing headline"
                      className="bg-black/30 border-white/10 h-10"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-white mb-2 block">CTA Text</label>
                    <Input
                      value={formData.cta_text}
                      onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                      placeholder="e.g., Get Started Now"
                      className="bg-black/30 border-white/10 h-10"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-white mb-2 block">Ad Copy</label>
                  <Textarea
                    value={formData.ad_copy}
                    onChange={(e) => setFormData({ ...formData, ad_copy: e.target.value })}
                    placeholder="Your persuasive ad copy..."
                    rows={3}
                    className="bg-black/30 border-white/10"
                  />
                </div>

                {/* Landing Page & CTA URL */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-white mb-2 block">Landing Page URL</label>
                    <Input
                      value={formData.landing_page_url}
                      onChange={(e) => setFormData({ ...formData, landing_page_url: e.target.value })}
                      placeholder="https://your-landing-page.com"
                      className="bg-black/30 border-white/10 h-10"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-white mb-2 block">CTA Destination URL</label>
                    <Input
                      value={formData.cta_url}
                      onChange={(e) => setFormData({ ...formData, cta_url: e.target.value })}
                      placeholder="https://..."
                      className="bg-black/30 border-white/10 h-10"
                    />
                  </div>
                </div>

                {/* Deployment Platforms */}
                <div className="glass rounded-xl p-4">
                  <label className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                    <Share2 className="w-4 h-4 text-cyan-400" />
                    Deployment Platforms
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {DEPLOYMENT_PLATFORMS.map((platform) => {
                      const Icon = platform.icon;
                      const isSelected = selectedPlatforms.includes(platform.id);
                      return (
                        <button
                          key={platform.id}
                          type="button"
                          onClick={() => togglePlatform(platform.id)}
                          disabled={platform.required}
                          className={cn(
                            "p-3 rounded-xl text-left transition-all flex items-center gap-3",
                            isSelected 
                              ? platform.required 
                                ? "bg-cyan-500/30 border-2 border-cyan-500/60" 
                                : "bg-purple-500/20 border-2 border-purple-500/50"
                              : "bg-white/5 border border-white/10 hover:bg-white/10",
                            platform.required && "cursor-default"
                          )}
                        >
                          <Icon className={cn(
                            "w-5 h-5 shrink-0",
                            isSelected ? (platform.required ? "text-cyan-400" : "text-purple-400") : "text-gray-400"
                          )} />
                          <div>
                            <p className={cn("text-sm font-medium", isSelected ? "text-white" : "text-gray-300")}>
                              {platform.name}
                              {platform.required && <span className="text-cyan-400 text-xs ml-1">(Required)</span>}
                            </p>
                            <p className="text-xs text-gray-500">{platform.description}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    Select additional platforms to cascade your ad. Orders will be created for managed services.
                  </p>
                </div>

                {/* Tags */}
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">Tags</label>
                  <Input
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="video-ad, cinematic, marketing"
                    className="bg-black/30 border-white/10 h-10"
                  />
                </div>

                {/* Submit Buttons */}
                <div className="flex gap-4 pt-4 border-t border-white/10">
                  <Button 
                    type="submit" 
                    className="flex-1 btn-primary bg-gradient-to-r from-cyan-500 to-purple-500 h-12 text-lg font-bold"
                    disabled={publishing}
                    data-testid="cinematic-publish-btn"
                  >
                    {publishing ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Deploy to Directory
                      </>
                    )}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowCinematicModal(false)}
                    className="border-white/20 h-12 px-6"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </DashboardLayout>
  );
}
